import { useQuery } from '@tanstack/react-query'
import { promoCodesApi, type ActiveDiscountResponse } from '@shared/api/promoCodes'
import { useAuthStore } from '@shared/store/authStore'

/**
 * useActiveDiscount — fetches the user's currently active promo discount (if any)
 * and caches it via React Query so the same data is reused across the app
 * (subscription plans screen, promo code card, etc).
 */
export function useActiveDiscount() {
  const user = useAuthStore((state) => state.user)

  const query = useQuery<ActiveDiscountResponse>({
    queryKey: ['active-discount', user?.id],
    queryFn: () => promoCodesApi.getActiveDiscount(),
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 минута
  })

  const data = query.data
  const discountPercent = data?.has_active_discount ? data.discount_percent ?? 0 : 0

  return {
    activeDiscount: data,
    discountPercent,
    hasActiveDiscount: discountPercent > 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

/**
 * applyDiscount — helper to compute a discounted price.
 * Mirrors the backend's behavior in subscription_payment/service.go::applyPromoDiscount,
 * including the floor of 1.
 */
export function applyDiscount(amount: number, discountPercent: number): number {
  if (!discountPercent || discountPercent <= 0) return amount
  const discounted = amount * (1 - discountPercent / 100)
  return discounted < 1 ? 1 : Math.round(discounted)
}
